import React, { useState } from 'react';
import { Mail, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { TextInput } from '../../../shared/components/TextInput';
import { authApi } from '../../../shared/lib/api';

interface ForgotPasswordFormProps {
  onBack: () => void;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [sentMessage, setSentMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setErrors({ email: 'E-mail é obrigatório' });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrors({ email: 'E-mail inválido' });
      return;
    }

    setErrors({});
    setIsLoading(true);
    try {
      // A resposta é a mesma exista ou não a conta — por isso mostramos o
      // estado de sucesso sem confirmar se o e-mail está cadastrado.
      const message = await authApi.forgotPassword(email.trim());
      setSentMessage(message);
    } catch (error: any) {
      setErrors({ submit: error?.message || 'Erro ao solicitar redefinição. Tente novamente.' });
    } finally {
      setIsLoading(false);
    }
  };

  if (sentMessage) {
    return (
      <div className="space-y-6">
        <div className="rounded-md bg-green-50 p-4 border border-green-200">
          <p className="text-sm text-green-800 flex items-start">
            <CheckCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
            <span>{sentMessage}</span>
          </p>
        </div>
        <p className="text-sm text-gray-600">
          O link expira em 60 minutos e só pode ser usado uma vez. Verifique também a caixa de spam.
        </p>
        <button
          type="button"
          onClick={onBack}
          className="w-full flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar para o login
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <p className="text-sm text-gray-600">
        Informe o e-mail cadastrado. Enviaremos um link para você definir uma nova senha.
      </p>

      <TextInput
        label="E-mail"
        type="email"
        icon={<Mail className="w-5 h-5" />}
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          if (errors.email) setErrors({});
        }}
        error={errors.email}
        autoComplete="email"
      />

      {errors.submit && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800 flex items-center">
            <AlertCircle className="w-4 h-4 mr-1" />
            {errors.submit}
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={isLoading}
        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isLoading ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white border-t-transparent mr-2"></div>
            Enviando...
          </>
        ) : 'Enviar link de redefinição'}
      </button>

      <button
        type="button"
        onClick={onBack}
        className="w-full flex justify-center items-center text-sm font-medium text-gray-600 hover:text-gray-800"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Voltar para o login
      </button>
    </form>
  );
};
